import {useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {TaxProfileField} from 'src/store/taxProfile/taxProfile.types';
import {TaxProfileQuestion} from 'src/store/taxProfile/taxProfile.types';
import {useActiveYear} from 'src/common/hooks/useActiveYear';
import {IP_PIN_LENGTH} from 'src/constants/constants';
import {TaxFormType} from 'src/store/taxForms/taxForms.types';
import {TaxFormFieldIds} from 'src/store/taxForms/taxForms.types';
import {selectTaxForms} from 'src/store/taxForms/taxForms.selector';
import {selectTaxProfile} from 'src/store/taxProfile/taxProfile.selector';
import {FILING_STATUS_ANSWER} from 'src/store/taxProfile/taxProfile.types';
import {IP_PIN_TYPES} from '../TaxReturns/TaxReturns.types';

const isValidIpPin = (pin: any) => {
  return typeof pin === 'string' && pin.length === IP_PIN_LENGTH;
};

export const useIPPinDetails = () => {
  const {activeYear} = useActiveYear();
  const {[activeYear]: taxProfile} = useSelector(selectTaxProfile);

  const tp = useMemo(() => {
    const questions = [
      TaxProfileQuestion.HAS_IP_PIN,
      TaxProfileQuestion.SELF_IP_PIN,
      TaxProfileQuestion.SPOUSE_IP_PIN,
      TaxProfileQuestion.FILING_STATUS,
      TaxProfileQuestion.LEGAL_NAME,
      TaxProfileQuestion.SPOUSE_LEGAL_NAME,
    ];
    const _info: any = {};

    questions.forEach((question) => {
      _info[question] = taxProfile.find(
        (q) => q.question_id === question,
      )?.answer;
    });

    return _info;
  }, [taxProfile]);

  const taxforms = useSelector(selectTaxForms);

  const dependents = useMemo(
    () => taxforms.filter((tf) => tf.formType === TaxFormType.DEPENDENT),
    [taxforms],
  );

  const getName = useCallback(
    (key: string | number) => {
      if (key === IP_PIN_TYPES.SELF) {
        const name = tp[TaxProfileQuestion.LEGAL_NAME];
        return `${name?.[TaxProfileField.FIRST_NAME]} ${
          name?.[TaxProfileField.LAST_NAME]
        }`;
      } else if (key === IP_PIN_TYPES.SPOUSE) {
        const name = tp[TaxProfileQuestion.SPOUSE_LEGAL_NAME];
        return `${name?.[TaxProfileField.FIRST_NAME]} ${
          name?.[TaxProfileField.LAST_NAME]
        } (Spouse)`;
      } else {
        const formData = dependents.find(
          (dep) => dep.taxFormId === key,
        )?.formData;
        return `${formData?.[TaxFormFieldIds.DEPENDENT_FIRST_NAME]} ${
          formData?.[TaxFormFieldIds.DEPENDENT_LAST_NAME]
        } (Dependant)`;
      }
    },
    [tp, dependents],
  );

  const {ipPins, hasSubmittedIpPins} = useMemo(() => {
    let _ipPins: {
      id: string | number;
      name: string;
      pin: string;
    }[] = [];

    const self = tp[TaxProfileQuestion.SELF_IP_PIN];
    const spouse = tp[TaxProfileQuestion.SPOUSE_IP_PIN];

    const depIds = dependents.map((dep) => dep.taxFormId).sort();

    const pinIds = [IP_PIN_TYPES.SELF, IP_PIN_TYPES.SPOUSE, ...depIds];

    pinIds.forEach((key) => {
      let pin = '';

      if (key === IP_PIN_TYPES.SELF) {
        pin = self;
      } else if (
        key === IP_PIN_TYPES.SPOUSE &&
        tp[TaxProfileField.FILING_STATUS] ===
          FILING_STATUS_ANSWER.MARRIED_FILING_JOINTLY
      ) {
        pin = spouse;
      } else {
        pin = dependents.find((dep) => dep.taxFormId === key)?.formData?.[
          TaxFormFieldIds.IP_PIN
        ];
      }

      if (isValidIpPin(pin)) {
        _ipPins.push({
          id: key,
          name: getName(key),
          pin,
        });
      }
    });

    let _hasSubmittedIpPins = false;
    const hasIpPin = tp[TaxProfileQuestion.HAS_IP_PIN];
    if (typeof hasIpPin === 'boolean' && !hasIpPin) {
      _hasSubmittedIpPins = true;
    }
    if (typeof hasIpPin === 'boolean' && hasIpPin && _ipPins.length > 0) {
      _hasSubmittedIpPins = true;
    }

    if (_hasSubmittedIpPins && !hasIpPin) {
      _ipPins = [];
    }

    return {hasSubmittedIpPins: _hasSubmittedIpPins, ipPins: _ipPins};
  }, [tp, dependents]);

  return {
    ipPins,
    hasSubmittedIpPins,
  };
};
